import { b2bService } from 'api/api'
import FormModal from 'components/forms/FormModal'
import React, { useMemo, useState } from 'react'
import { PartnerPageData, Workflow } from 'types/types'
import { AxiosError, HttpStatusCode } from 'axios'
import { getCookie } from 'cookies-next'

const FormModalContext = React.createContext({
  gate: '',
  openModal: (gate: string) => {},
  closeModal: () => {},
})

type FormModalProviderProps = {
  page: PartnerPageData
  children: React.ReactNode
}

function FormModalProvider({ children, page }: FormModalProviderProps) {
  const [isOpened, setIsOpened] = useState(false)
  const [gate, setGate] = useState('')
  const [data, setData] = useState<Workflow | null>(null)

  const handleOpenModal = async (gateId: string) => {
    const isAuthenticated =
      getCookie('accessToken', {
        sameSite: process.env.NODE_ENV === 'production' ? 'none' : 'lax',
        secure: process.env.NODE_ENV === 'production',
      }) !== null

    setGate(gateId)

    if (isAuthenticated) {
      await b2bService
        .getWorkflow(page.game_id, page.company_domain, gateId)
        .then((res) => {
          if (res.questions.length) {
            setIsOpened(true)
            setData(res)
          }
        })
        .catch((err: AxiosError<any>) => {
          // skip data collection if there are no questions left OR game is started and user is not joined to the game
          if (
            err.response?.status === HttpStatusCode.NotFound ||
            err.response?.status === HttpStatusCode.Forbidden
          ) {
            setIsOpened(false)
            setData(null)
          }
        })
    }
  }

  const handleCloseModal = () => {
    setIsOpened(false)
  }

  const values = useMemo(
    () => ({ gate, openModal: handleOpenModal, closeModal: handleCloseModal }),
    [handleOpenModal],
  )

  return (
    <FormModalContext.Provider value={values}>
      {data && isOpened ? (
        <FormModal
          page={page}
          open={true}
          formProps={{
            workflow: data,
            gate,
            onFormSubmit: handleCloseModal,
            gameId: page.game_id,
          }}
        />
      ) : null}
      {children}
    </FormModalContext.Provider>
  )
}
export default FormModalProvider

export const useFormModalContext = () => React.useContext(FormModalContext)
